import axios, { AxiosResponse } from "axios";

import config, { context_root } from "app.config";
import { Pageable } from "types/common/Pageable";

axios.defaults.baseURL = config.apiUrl || "";

/**
 * @template T - The type of the entity
 * @template Y - Supported query params
 * @template ID - The type of the entity primary key
 */
export class Api<T, Y = never, ID = number | string> {
  url: string;

  rootWrapper?: string;

  /**
   * Create a new CRUD axios API connector.
   *
   * @param path The path to the resources (not including the context path)
   * @param rootWrapper Wrap the JSON object with another object having this value as a key
   */
  constructor(path: string, rootWrapper?: string) {
    if (path.startsWith("/")) {
      this.url = context_root + path;
    } else {
      this.url = `${context_root}/${path}`;
    }
    this.rootWrapper = rootWrapper;
  }

  findAll(query?: Y): Promise<AxiosResponse<Pageable<T>>> {
    return axios.get(this.url, { params: query });
  }

  findById(id: ID, query?: Y): Promise<AxiosResponse<T>> {
    return axios
      .get(`${this.url}/${id}`, { params: query })
      .then((resp) => this.unwrapData(resp));
  }

  create(entity: T): Promise<AxiosResponse<T>> {
    return axios
      .post(this.url, this.wrap(entity))
      .then((resp) => this.unwrapData(resp));
  }

  update(id: ID, entity: T): Promise<AxiosResponse<T>> {
    return axios
      .put(`${this.url}/${id}`, this.wrap(entity))
      .then((resp) => this.unwrapData(resp));
  }

  deleteById(id: ID): Promise<AxiosResponse<never>> {
    return axios.delete(`${this.url}/${id}`);
  }

  private wrap(entity: T): unknown {
    if (this.rootWrapper === undefined) {
      return entity;
    }
    const wrapped = {};
    wrapped[this.rootWrapper] = entity;
    return wrapped;
  }

  private unwrap(data: unknown): T {
    if (
      this.rootWrapper &&
      data &&
      typeof data === "object" &&
      this.rootWrapper in data
    ) {
      return data[this.rootWrapper] as T;
    }
    return data as T;
  }

  private unwrapData(response: AxiosResponse<T>): AxiosResponse<T> {
    response.data = this.unwrap(response.data);
    return response;
  }
}
